import { z } from 'zod';
import { useContext, useState } from 'react';

import routes from 'utils/routes';
import Form from 'components/form';
import SEO from 'components/SEO';
import AuthContext from 'context/AuthContext';
import useFormHandler from 'hooks/useFormHandler';
import TermsAndPolicy from 'components/TermsAndPolicy';
import { createEmailSchema } from 'utils/schemaBuilder';
import { logger } from '../../utils/logger';

const ForgotPassword = () => {
  const {
    actions: { forgotPassword },
  } = useContext(AuthContext);
  const [success, setSuccess] = useState<string | null>(null);
  const formSchema = z.object({
    email: createEmailSchema(true), // Email is required for password reset
  });

  const { form, loading, error, isValid, setError } =
    useFormHandler(formSchema);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setSuccess(null);
      await forgotPassword(values.email);
      
      // Log successful password reset request
      logger.info('Password reset email sent successfully');
      
      setSuccess(
        'If an account exists with this email, a password reset link was sent'
      );
    } catch (error) {
      // Log password reset request failures
      logger.authError('Password reset request failed', error as Error, {
        component: 'ForgotPasswordPage'
      });
      
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <>
      <SEO 
        title="Forgot Password - Reset Your AI Receptionist Account"
        description="Reset your Nucleus AI account password. Enter your email to receive a secure password reset link and regain access to your AI receptionist dashboard."
        keywords="forgot password, password recovery, AI receptionist account, account access"
      />
    <div className="flex flex-col min-h-screen">
      <nav className="h-[70px] w-full px-4 md:px-6 flex items-center justify-between">
        <a
          href={routes.dashboardPath.href}
          className="flex items-center gap-4"
          tabIndex={5}
        >
          <img src="/images/nucleus-logo.svg" alt="Nucleus Logo" className="h-8" />
        </a>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 md:px-0">
        <div className="mx-auto">
          <div className="text-center mb-3 max-w-[600px] mx-auto">
            <h1 className="text-xl md:text-2xl font-semibold text-[#5E6AB2] mb-1">
              Forgot Password - Reset Your AI Receptionist Account
            </h1>
            <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-4 text-center">
              Enter your email and we'll send you a link to reset it
            </h2>
          </div>
          <div className="border custom-border-color bg-white rounded-xl p-4 md:p-6 md:w-5/6 xl:w-[440px] mx-auto">
            <Form
              form={form}
              error={error}
              success={success}
              onSubmit={onSubmit}
            >
              <Form.Input
                type="email"
                name="email"
                label="Email"
                placeholder="Email"
                tabIndex={1}
              />
              <Form.Submit loading={loading} disabled={!isValid || loading} tabIndex={2}>
                Reset password
              </Form.Submit>
            </Form>
          </div>
        </div>
      </div>
      <div className="h-[50px]">
        <TermsAndPolicy termsTabIndex={3} privacyTabIndex={4} />
      </div>
    </div>
    </>
  );
};

export default ForgotPassword;
